import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { C, FONT_NUM } from './tokens';
import { regimeStyle } from './formatters';
import { MobileSummaryCard } from './atoms';
import { BreakoutSummarySkeleton } from './skeletons';
import { breakoutStatsShape } from './shapes';

// Compact summary grid shown on mobile (above the candidates table).
const BreakoutSummaryMobile = ({
  candidates,
  stats,
  dominantRegimeCode,
  t,
  getMetadataLabel,
}) => (
  <Box sx={{ py: 1.1, borderBottom: `1px solid ${C.borderSoft}` }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.9 }}>
      <MobileSummaryCard
        label={t('breakoutCandidates.summary.candidates')}
        value={String(candidates.length)}
        subtext={`${stats.buy} ${getMetadataLabel('signals', 'BUY')} · ${stats.watch} ${getMetadataLabel('signals', 'BUY_WATCH', 'short_label')}`}
      />
      <MobileSummaryCard
        label={t('breakoutCandidates.summary.avg')}
        value={stats.avg.toFixed(1)}
        accent={C.blue}
        subtext={`${t('breakoutCandidates.summary.max')} ${stats.max.toFixed(1)}`}
      />
      <MobileSummaryCard
        label={t('breakoutCandidates.summary.top')}
        value={stats.topSector ? stats.topSector[0] : '—'}
        subtext={stats.topSector ? `${stats.topSector[1]} ${t('breakoutCandidates.summary.candidates')}` : undefined}
      />
      <MobileSummaryCard
        label={t('breakoutCandidates.drawer.regime')}
        value={dominantRegimeCode ? getMetadataLabel('marketRegimes', dominantRegimeCode) : '—'}
        subtext={Object.entries(stats.regimeCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 2)
          .map(([key, count]) => `${count} ${getMetadataLabel('marketRegimes', key)}`)
          .join(' · ')}
      />
    </Box>
  </Box>
);

BreakoutSummaryMobile.propTypes = {
  candidates: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  stats: breakoutStatsShape.isRequired,
  dominantRegimeCode: PropTypes.string,
  t: PropTypes.func.isRequired,
  getMetadataLabel: PropTypes.func.isRequired,
};

BreakoutSummaryMobile.defaultProps = {
  dominantRegimeCode: null,
};

// Desktop horizontal summary strip showing total / signal split / regime dots / stats.
const BreakoutSummaryDesktop = ({ candidates, stats, t, getMetadataLabel }) => {
  const total = candidates.length;
  const otherCount = Math.max(0, total - stats.buy - stats.watch);
  const hasItems = total > 0;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        py: 1,
        borderBottom: `1px solid ${C.borderSoft}`,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.1, sm: 2.5 }, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: C.muted, fontWeight: 700, whiteSpace: 'nowrap' }}>
          <span style={{ color: C.ink, fontWeight: 800, fontFamily: FONT_NUM }}>{total}</span>
          {' '}{t('breakoutCandidates.summary.candidates')}
        </Typography>

        {hasItems ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: '100%', sm: 200 } }}>
            <Tooltip
              arrow
              title={
                <Box sx={{ p: 0.4 }}>
                  <div>{getMetadataLabel('signals', 'BUY')}: <b>{stats.buy}</b></div>
                  <div>{getMetadataLabel('signals', 'BUY_WATCH')}: <b>{stats.watch}</b></div>
                  {otherCount > 0 ? (
                    <div>{t('breakoutCandidates.summary.other')}: <b>{otherCount}</b></div>
                  ) : null}
                </Box>
              }
            >
              <Box sx={{
                display: 'flex',
                height: 5,
                width: 130,
                borderRadius: 99,
                overflow: 'hidden',
                bgcolor: C.borderSoft,
              }}>
                <Box sx={{ width: `${(stats.buy / total) * 100}%`, bgcolor: C.green }} />
                <Box sx={{ width: `${(stats.watch / total) * 100}%`, bgcolor: C.blue }} />
                <Box sx={{ width: `${(otherCount / total) * 100}%`, bgcolor: '#cbd5e1' }} />
              </Box>
            </Tooltip>
            <Typography sx={{ fontSize: 11.5, fontFamily: FONT_NUM, fontWeight: 700, whiteSpace: 'nowrap' }}>
              <span style={{ color: C.green }}>{stats.buy} {getMetadataLabel('signals', 'BUY')}</span>
              <span style={{ color: C.mutedSoft }}>{' · '}</span>
              <span style={{ color: C.blue }}>{stats.watch} {getMetadataLabel('signals', 'BUY_WATCH', 'short_label')}</span>
            </Typography>
          </Box>
        ) : null}

        {hasItems ? (
          <Stack direction="row" spacing={0.8} alignItems="center">
            {['RISK_ON', 'NARROW_RALLY', 'RECOVERY', 'RISK_OFF']
              .filter((k) => (stats.regimeCount[k] || 0) > 0)
              .map((k) => {
                const s = regimeStyle(k);
                return (
                  <Tooltip
                    key={k}
                    title={t('breakoutCandidates.summary.regimeCount', {
                      regime: getMetadataLabel('marketRegimes', k),
                      count: stats.regimeCount[k],
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: s.fg }} />
                      <Typography sx={{ fontSize: 11.5, fontFamily: FONT_NUM, fontWeight: 700, color: C.inkMid }}>
                        {stats.regimeCount[k]}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
          </Stack>
        ) : null}

        {hasItems ? (
          <Typography sx={{ fontSize: 11.5, color: C.muted, fontFamily: FONT_NUM, whiteSpace: 'nowrap' }}>
            {t('breakoutCandidates.summary.avg')}{' '}
            <span style={{ color: C.ink, fontWeight: 700 }}>{stats.avg.toFixed(1)}</span>
            <span style={{ color: C.mutedSoft }}>{'  ·  '}</span>
            {t('breakoutCandidates.summary.max')}{' '}
            <span style={{ color: C.green, fontWeight: 700 }}>{stats.max.toFixed(1)}</span>
          </Typography>
        ) : null}

        {stats.topSector ? (
          <Typography sx={{ fontSize: 11.5, color: C.muted, whiteSpace: 'nowrap' }}>
            {t('breakoutCandidates.summary.top')}{' '}
            <span style={{ color: C.ink, fontWeight: 700 }}>{stats.topSector[0]}</span>
            <span style={{ color: C.mutedSoft }}> ({stats.topSector[1]})</span>
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

BreakoutSummaryDesktop.propTypes = {
  candidates: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  stats: breakoutStatsShape.isRequired,
  t: PropTypes.func.isRequired,
  getMetadataLabel: PropTypes.func.isRequired,
};

// Top-level dispatcher: skeleton during initial load, then mobile or desktop variant.
const BreakoutSummaryHeader = ({
  loading,
  candidates,
  isCompact,
  stats,
  dominantRegimeCode,
  t,
  getMetadataLabel,
}) => {
  if (loading && candidates.length === 0) {
    return <BreakoutSummarySkeleton isCompact={isCompact} />;
  }
  if (isCompact) {
    return (
      <BreakoutSummaryMobile
        candidates={candidates}
        stats={stats}
        dominantRegimeCode={dominantRegimeCode}
        t={t}
        getMetadataLabel={getMetadataLabel}
      />
    );
  }
  return (
    <BreakoutSummaryDesktop
      candidates={candidates}
      stats={stats}
      t={t}
      getMetadataLabel={getMetadataLabel}
    />
  );
};

BreakoutSummaryHeader.propTypes = {
  loading: PropTypes.bool,
  candidates: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  isCompact: PropTypes.bool,
  stats: breakoutStatsShape.isRequired,
  dominantRegimeCode: PropTypes.string,
  t: PropTypes.func.isRequired,
  getMetadataLabel: PropTypes.func.isRequired,
};

BreakoutSummaryHeader.defaultProps = {
  loading: false,
  isCompact: false,
  dominantRegimeCode: null,
};

export default BreakoutSummaryHeader;
